import { createContext, useContext } from 'react'
import { type TrackConfig, trackInteraction } from './tracking'

interface SegmentedControlContextValue {
  value: string
  onChange?: (value: string) => void
}

const SegmentedControlContext = createContext<SegmentedControlContextValue | null>(null)

function Root({
  value,
  onChange,
  children,
  track,
}: {
  value: string
  onChange?: (value: string) => void
  children: React.ReactNode
  track?: TrackConfig<{ value: string }>
}) {
  const handleChange = (nextValue: string) => {
    onChange?.(nextValue)
    if (onChange && nextValue !== value) trackInteraction(track, { value: nextValue })
  }

  return (
    <SegmentedControlContext.Provider
      value={{ value, onChange: onChange ? handleChange : undefined }}
    >
      <div className="relative grid h-10 auto-cols-fr grid-flow-col rounded-lg border-2 border-gray-stroke bg-white text-sm leading-[1.5]">
        {children}
      </div>
    </SegmentedControlContext.Provider>
  )
}

interface SegmentedControlOptionProps {
  value: string
  disabled?: boolean
  children: React.ReactNode
}

function Option({ value, disabled, children }: SegmentedControlOptionProps) {
  const ctx = useContext(SegmentedControlContext)
  if (!ctx) throw new Error('SegmentedControl.Option must be used inside SegmentedControl.Root')

  const selected = ctx.value === value
  const className = selected
    ? '-m-0.5 flex h-10 items-center justify-center rounded-lg border-2 border-forest bg-mint-25 font-semibold text-forest'
    : 'flex h-full items-center justify-center font-medium text-charcoal-50'

  const handleClick = () => {
    ctx.onChange?.(value)
  }

  return (
    <button
      type="button"
      className={className}
      disabled={disabled || !ctx.onChange}
      onClick={handleClick}
    >
      {children}
    </button>
  )
}

export const SegmentedControl = { Root, Option }
